import React from 'react';

/**
 * Review Schema Markup Generator
 * Generates JSON-LD structured data for product reviews
 */

export interface ReviewData {
  author: string;
  datePublished: string;
  reviewBody: string;
  reviewRating: number;
}

export interface AggregateReviewData {
  productName: string;
  averageRating: number;
  reviewCount: number;
  reviews?: ReviewData[];
}

/**
 * Generate individual Review schema
 */
export function generateReviewSchema(review: ReviewData, productName: string) {
  return {
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.author,
    },
    datePublished: review.datePublished,
    reviewBody: review.reviewBody,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.reviewRating,
      bestRating: 5,
    },
    itemReviewed: {
      '@type': 'Product',
      name: productName,
    },
  };
}

/**
 * Generate AggregateRating schema for products
 */
export function generateAggregateRatingSchema(data: AggregateReviewData) {
  const schema: any = {
    '@type': 'AggregateRating',
    ratingValue: data.averageRating.toFixed(1),
    reviewCount: data.reviewCount,
    bestRating: 5,
    worstRating: 1,
  };

  return schema;
}

/**
 * Generate complete review collection schema
 */
export function generateReviewCollectionSchema(data: AggregateReviewData) {
  const reviews = data.reviews?.map((review) =>
    generateReviewSchema(review, data.productName)
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data.productName,
    aggregateRating: generateAggregateRatingSchema(data),
    ...(reviews && reviews.length > 0 && { review: reviews }),
  };
}

/**
 * Review Schema Script Component
 */
export function ReviewSchemaScript({ data }: { data: AggregateReviewData }) {
  const schema = generateReviewCollectionSchema(data);

  return (
    <script
      type="application/ld+json"
      // Review bodies are customer-written. A "</script>" inside one would
      // close this tag and run whatever follows, so `<` is escaped.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, '\\u003c') }}
    />
  );
}
